// Database-type picker for a BI connection.
//
// Replaces the old 220px <select>: at 25 offered types a flat dropdown is
// unusable. This is a searchable, sectioned card grid whose ENTIRE contents
// come from GET /connections/types — which derives every field (label, badge,
// section, default port, credential form, driver availability) from the ONE
// registry in twasta_runtime_engine/bi_dialect.py. There is deliberately no
// type list in this file: a database added to that registry appears here with
// no frontend change.
//
// Section order: "In use in your tenant" (pinned top, sorted by usage, hidden
// when empty) → "Popular" (so a new tenant doesn't open to a cold grid) →
// the registry's own categories in their declared order.

import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from '@/components/common/Modal'
import type { ConnectionType, ConnectionTypeCategory } from '@/api/connections'

// Shown to a tenant with no connections yet, in this order. Purely a
// cold-start affordance — every one of these is also in its own category
// section below, so nothing here decides what is *offered*.
const POPULAR = ['postgresql', 'snowflake', 'databricks', 'bigquery',
  'mysql', 'redshift', 'clickhouse', 'mssql']

interface Section {
  key: string
  label: string
  hint?: string
  types: ConnectionType[]
}

interface Props {
  open: boolean
  types: ConnectionType[]
  categories: ConnectionTypeCategory[]
  /** Currently selected DATABASE_TYPE, highlighted in the grid. */
  value?: string
  loading?: boolean
  error?: string | null
  onSelect: (dbType: string) => void
  onClose: () => void
}

/** Match a query against the label, the stored value and the dialect, so
 *  "pg", "postgres" and "postgresql" all find PostgreSQL. */
function matches(t: ConnectionType, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  return [t.label, t.value, t.dialect, t.description]
    .some((field) => (field || '').toLowerCase().includes(needle))
}

function buildSections(
  types: ConnectionType[],
  categories: ConnectionTypeCategory[],
  query: string,
): Section[] {
  const visible = types.filter((t) => matches(t, query))
  const sections: Section[] = []

  // Searching collapses the sections — when someone types, they want the
  // matches, not a lesson in how we file databases.
  if (query.trim()) {
    return visible.length
      ? [{ key: 'results', label: `${visible.length} match${visible.length === 1 ? '' : 'es'}`, types: visible }]
      : []
  }

  const inUse = visible.filter((t) => t.in_use > 0)
    .sort((a, b) => b.in_use - a.in_use || a.label.localeCompare(b.label))
  if (inUse.length) {
    sections.push({
      key: 'in-use',
      label: 'In use in your tenant',
      hint: 'Databases your projects already connect to.',
      types: inUse,
    })
  } else {
    // Only worth showing the cold-start shortlist when there is nothing real
    // to pin — otherwise it is just a duplicate of the sections below.
    const popular = POPULAR
      .map((v) => visible.find((t) => t.value === v))
      .filter((t): t is ConnectionType => Boolean(t))
    if (popular.length) {
      sections.push({ key: 'popular', label: 'Popular', types: popular })
    }
  }

  for (const cat of categories) {
    const inCat = visible.filter((t) => t.category === cat.key)
    if (inCat.length) sections.push({ key: cat.key, label: cat.label, types: inCat })
  }
  return sections
}

export default function DatabaseTypePickerModal({
  open, types, categories, value, loading, error, onSelect, onClose,
}: Props) {
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const sections = useMemo(
    () => buildSections(types, categories, query),
    [types, categories, query],
  )
  // Flattened in render order, so arrow keys walk the grid as it reads.
  const flat = useMemo(() => sections.flatMap((s) => s.types), [sections])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setCursor(Math.max(0, types.findIndex((t) => t.value === value)))
    // Autofocus search: the fastest path for someone who knows what they want.
    const id = window.setTimeout(() => searchRef.current?.focus(), 40)
    return () => window.clearTimeout(id)
  }, [open, value, types])

  useEffect(() => { setCursor(0) }, [query])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!flat.length) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      setCursor((c) => Math.min(flat.length - 1, c + 1))
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      setCursor((c) => Math.max(0, c - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const picked = flat[cursor]
      if (picked) onSelect(picked.value)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Choose a database" size="wide">
      <div onKeyDown={onKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search databases — name, dialect, or what it's for"
            aria-label="Search databases"
            style={{
              width: '100%', padding: '10px 12px', fontSize: 15.5,
              border: '1px solid var(--color-border)', borderRadius: 8,
              background: 'var(--color-bg)', color: 'var(--color-text)',
            }}
          />
        </div>

        {loading && (
          <p style={{ margin: 0, fontSize: 15, color: 'var(--color-text-secondary)' }}>
            Loading databases…
          </p>
        )}

        {error && !loading && (
          <p style={{
            margin: 0, fontSize: 15, color: 'var(--color-error)',
            padding: '10px 12px', borderRadius: 8,
            border: '1px solid var(--color-error)',
          }}>
            {error}
          </p>
        )}

        {!loading && !error && !sections.length && (
          <p style={{ margin: '20px 0', fontSize: 15, color: 'var(--color-text-secondary)' }}>
            No database matches “{query}”.
          </p>
        )}

        <div style={{ maxHeight: '58vh', overflowY: 'auto', paddingRight: 4 }}>
          {sections.map((section) => (
            <section key={section.key} style={{ marginBottom: 22 }}>
              <header style={{ marginBottom: 10 }}>
                <h3 style={{
                  margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: 0.6,
                  textTransform: 'uppercase', color: 'var(--color-text-secondary)',
                }}>
                  {section.label}
                </h3>
                {section.hint && (
                  <p style={{ margin: '3px 0 0', fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
                    {section.hint}
                  </p>
                )}
              </header>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 10,
              }}>
                {section.types.map((t) => (
                  <TypeCard
                    key={`${section.key}:${t.value}`}
                    type={t}
                    selected={t.value === value}
                    focused={flat[cursor]?.value === t.value}
                    onPick={() => onSelect(t.value)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Modal>
  )
}

function TypeCard({ type, selected, focused, onPick }: {
  type: ConnectionType
  selected: boolean
  focused: boolean
  onPick: () => void
}) {
  const border = selected
    ? 'var(--color-primary)'
    : focused ? 'var(--color-text-secondary)' : 'var(--color-border)'
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      style={{
        display: 'flex', gap: 11, alignItems: 'flex-start', textAlign: 'left',
        width: '100%', padding: '11px 12px', cursor: 'pointer',
        border: `1px solid ${border}`,
        outline: selected ? '1px solid var(--color-primary)' : 'none',
        borderRadius: 9, background: 'var(--color-surface)',
        color: 'var(--color-text)',
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 30, height: 30, borderRadius: 7,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 700, letterSpacing: 0.3,
          background: 'var(--color-bg-secondary)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {type.badge}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{
          display: 'block', fontSize: 15, fontWeight: 600, marginBottom: 2,
        }}>
          {type.label}
        </span>
        <span style={{
          display: 'block', fontSize: 13.5, lineHeight: 1.35,
          color: 'var(--color-text-secondary)',
        }}>
          {type.description}
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
          {type.in_use > 0 && (
            <Chip tone="accent">
              {type.in_use} connection{type.in_use === 1 ? '' : 's'}
            </Chip>
          )}
          {type.warehouse && <Chip>Read-only source</Chip>}
          {/* A missing driver never disables the card: the connection form and
              Test give the exact pip command, and greying it out would hide
              the fix rather than surface it. */}
          {!type.driver_installed && <Chip tone="warn">Driver not installed</Chip>}
        </span>
      </span>
    </button>
  )
}

function Chip({ children, tone }: { children: React.ReactNode; tone?: 'accent' | 'warn' }) {
  const color = tone === 'accent' ? 'var(--color-primary)'
    : tone === 'warn' ? 'var(--color-warning, #b26a00)'
      : 'var(--color-text-secondary)'
  return (
    <span style={{
      fontSize: 13, fontWeight: 500, padding: '2px 7px', borderRadius: 20,
      border: `1px solid ${color}`, color, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}
